import React, { useState, useEffect, useCallback } from 'react';
import GameHUD from '../shared/GameHUD';
import PauseMenu from '../shared/PauseMenu';
import GameComplete from '../GameComplete';
import { shuffleArray, calculateRewards } from '../../../services/gameService';
import './CardBattlerGame.css';

// Generate cards from trivia-style content
function generateBattleCards(gameContent) {
    const cards = [];

    // Use trivia rounds if available, or bonusQuestions
    const questions = gameContent?.rounds?.[0]?.questions ||
                     gameContent?.bonusQuestions ||
                     gameContent?.questions || [];

    questions.forEach((q, index) => {
        // Create a fact card from the correct answer
        cards.push({
            id: `fact-${index}`,
            type: 'fact',
            title: q.correctAnswer || q.options?.[0],
            description: q.explanation || q.question,
            power: 15 + Math.floor(Math.random() * 10),
            defense: 5 + Math.floor(Math.random() * 5),
            emoji: ['📚', '💡', '🎯', '✨', '🔬'][index % 5]
        });
    });

    return cards.slice(0, 10); // Limit to 10 cards
}

function CardBattlerGame({
    gameContent,
    template,
    topic,
    difficulty = 'intermediate',
    onComplete,
    onQuit
}) {
    // Game state
    const [playerHand, setPlayerHand] = useState([]);
    const [playerDeck, setPlayerDeck] = useState([]);
    const [playerHealth, setPlayerHealth] = useState(100);
    const [enemyHealth, setEnemyHealth] = useState(80);
    const [maxPlayerHealth, setMaxPlayerHealth] = useState(100);
    const [maxEnemyHealth, setMaxEnemyHealth] = useState(80);
    const [score, setScore] = useState(0);
    const [round, setRound] = useState(1);
    const [cardsPlayed, setCardsPlayed] = useState(0);
    const [xpEarned, setXpEarned] = useState(0);
    const [coinsEarned, setCoinsEarned] = useState(0);

    // Battle state
    const [selectedCard, setSelectedCard] = useState(null);
    const [enemyCard, setEnemyCard] = useState(null);
    const [battlePhase, setBattlePhase] = useState('select'); // select, battle, result
    const [battleResult, setBattleResult] = useState(null);
    const [turnWinner, setTurnWinner] = useState(null);

    // UI state
    const [isPaused, setIsPaused] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [victory, setVictory] = useState(false);
    const [showIntro, setShowIntro] = useState(true);

    // Enemy cards pool
    const enemyCards = [
        { id: 'myth-1', type: 'myth', title: 'Common Misconception', power: 12, defense: 4, emoji: '👻' },
        { id: 'myth-2', type: 'myth', title: 'False Belief', power: 14, defense: 3, emoji: '🎭' },
        { id: 'myth-3', type: 'myth', title: 'Urban Legend', power: 10, defense: 6, emoji: '🌙' },
        { id: 'myth-4', type: 'myth', title: 'Old Wives Tale', power: 11, defense: 5, emoji: '🔮' },
        { id: 'myth-5', type: 'myth', title: 'Fake News', power: 16, defense: 2, emoji: '📰' },
    ];

    // Initialize game
    useEffect(() => {
        if (gameContent) {
            const cards = generateBattleCards(gameContent);
            const shuffled = shuffleArray(cards);

            // Deal initial hand (5 cards)
            setPlayerHand(shuffled.slice(0, 5));
            setPlayerDeck(shuffled.slice(5));

            // Set difficulty
            const diffSettings = template?.difficultyScaling?.[difficulty] || {};
            const pHealth = diffSettings.playerHealth || 100;
            const eHealth = diffSettings.enemyHealth || 80;
            setPlayerHealth(pHealth);
            setMaxPlayerHealth(pHealth);
            setEnemyHealth(eHealth);
            setMaxEnemyHealth(eHealth);

            // Hide intro after delay
            setTimeout(() => setShowIntro(false), 2500);
        }
    }, [gameContent, template, difficulty]);

    const handleGameOver = useCallback((won) => {
        setVictory(won);
        setIsComplete(true);

        const healthPercent = Math.round((playerHealth / maxPlayerHealth) * 100);
        const rewards = calculateRewards(template, cardsPlayed, healthPercent);

        setXpEarned(rewards.xp);
        setCoinsEarned(rewards.coins);

        if (onComplete) {
            onComplete({
                victory: won,
                score,
                xp: rewards.xp,
                coins: rewards.coins,
                stars: rewards.stars,
                accuracy: healthPercent,
                stats: {
                    rounds: round,
                    cardsPlayed,
                    remainingHealth: playerHealth
                }
            });
        }
    }, [score, round, cardsPlayed, playerHealth, maxPlayerHealth, template, onComplete]);

    // Check for game over
    useEffect(() => {
        if (playerHealth <= 0) {
            handleGameOver(false);
        } else if (enemyHealth <= 0) {
            handleGameOver(true);
        }
    }, [playerHealth, enemyHealth, handleGameOver]);

    const selectCard = (card) => {
        if (battlePhase !== 'select') return;
        setSelectedCard(card);
    };

    const playCard = () => {
        if (!selectedCard || battlePhase !== 'select') return;

        setBattlePhase('battle');

        // Enemy draws a random card
        const enemy = enemyCards[Math.floor(Math.random() * enemyCards.length)];
        setEnemyCard(enemy);

        // Calculate battle after animation
        setTimeout(() => calculateBattle(selectedCard, enemy), 1500);
    };

    const calculateBattle = (playerCard, enemy) => {
        const playerTotal = playerCard.power + Math.floor(Math.random() * 5);
        const enemyTotal = enemy.power + Math.floor(Math.random() * 5);

        let result;
        let winner;

        if (playerTotal > enemyTotal) {
            // Player wins - damage enemy
            const damage = playerTotal - enemy.defense;
            setEnemyHealth(prev => Math.max(0, prev - damage));
            setScore(prev => prev + 100 + damage);
            setXpEarned(prev => prev + 15);
            result = { type: 'win', damage, message: `Your knowledge dealt ${damage} damage!` };
            winner = 'player';
        } else if (enemyTotal > playerTotal) {
            // Enemy wins - damage player
            const damage = enemyTotal - playerCard.defense;
            setPlayerHealth(prev => Math.max(0, prev - damage));
            result = { type: 'lose', damage, message: `The myth dealt ${damage} damage to you!` };
            winner = 'enemy';
        } else {
            // Draw
            result = { type: 'draw', damage: 0, message: 'It\'s a draw! No damage dealt.' };
            winner = 'draw';
        }

        setBattleResult(result);
        setTurnWinner(winner);
        setBattlePhase('result');
        setCardsPlayed(prev => prev + 1);
    };

    const nextTurn = () => {
        // Remove played card from hand
        setPlayerHand(prev => prev.filter(c => c.id !== selectedCard.id));

        // Draw new card if deck has cards
        if (playerDeck.length > 0) {
            setPlayerHand(prev => [...prev, playerDeck[0]]);
            setPlayerDeck(prev => prev.slice(1));
        }

        setSelectedCard(null);
        setEnemyCard(null);
        setBattleResult(null);
        setTurnWinner(null);
        setBattlePhase('select');
        setRound(prev => prev + 1);

        // Check if player is out of cards
        if (playerHand.length <= 1 && playerDeck.length === 0) {
            // Out of cards - game over based on who has more health
            setTimeout(() => handleGameOver(playerHealth > enemyHealth), 500);
        }
    };

    const handleRestart = () => {
        const cards = generateBattleCards(gameContent);
        const shuffled = shuffleArray(cards);

        setPlayerHand(shuffled.slice(0, 5));
        setPlayerDeck(shuffled.slice(5));
        setPlayerHealth(maxPlayerHealth);
        setEnemyHealth(maxEnemyHealth);
        setScore(0);
        setRound(1);
        setCardsPlayed(0);
        setXpEarned(0);
        setCoinsEarned(0);
        setSelectedCard(null);
        setEnemyCard(null);
        setBattlePhase('select');
        setBattleResult(null);
        setTurnWinner(null);
        setIsPaused(false);
        setIsComplete(false);
        setVictory(false);
        setShowIntro(true);
        setTimeout(() => setShowIntro(false), 2500);
    };

    if (!gameContent) {
        return (
            <div className="cardbattle-loading">
                <div className="loading-spinner">🃏</div>
                <p>Preparing your deck...</p>
            </div>
        );
    }

    // Intro screen
    if (showIntro) {
        return (
            <div className="cardbattle-intro">
                <div className="intro-content">
                    <div className="vs-display">
                        <div className="player-side">
                            <span className="side-icon">📚</span>
                            <span className="side-label">Knowledge</span>
                        </div>
                        <span className="vs-text">VS</span>
                        <div className="enemy-side">
                            <span className="side-icon">👻</span>
                            <span className="side-label">Myths</span>
                        </div>
                    </div>
                    <h2>Defeat the Myths with Facts!</h2>
                    <p>Play your knowledge cards to deal damage</p>
                </div>
            </div>
        );
    }

    return (
        <div className="cardbattle-game">
            <GameHUD
                gameName="Knowledge Duel"
                score={score}
                xp={xpEarned}
                coins={coinsEarned}
                onPause={() => setIsPaused(true)}
                showLives={false}
                showTimer={false}
            />

            {/* Health Bars */}
            <div className="battle-arena">
                <div className="health-display player-health">
                    <span className="health-label">📚 You</span>
                    <div className="health-bar">
                        <div
                            className="health-fill player"
                            style={{ width: `${(playerHealth / maxPlayerHealth) * 100}%` }}
                        />
                    </div>
                    <span className="health-value">{playerHealth}/{maxPlayerHealth}</span>
                </div>

                <div className="round-indicator">
                    Round {round}
                </div>

                <div className="health-display enemy-health">
                    <span className="health-label">👻 Myth Master</span>
                    <div className="health-bar">
                        <div
                            className="health-fill enemy"
                            style={{ width: `${(enemyHealth / maxEnemyHealth) * 100}%` }}
                        />
                    </div>
                    <span className="health-value">{enemyHealth}/{maxEnemyHealth}</span>
                </div>
            </div>

            {/* Battle Zone */}
            <div className="battle-zone">
                {battlePhase !== 'select' && (
                    <div className="battle-cards">
                        <div className={`battle-card player-card ${turnWinner === 'player' ? 'winner' : turnWinner === 'enemy' ? 'loser' : ''}`}>
                            <span className="card-emoji">{selectedCard?.emoji}</span>
                            <span className="card-title">{selectedCard?.title}</span>
                            <div className="card-stats">
                                <span>⚔️ {selectedCard?.power}</span>
                                <span>🛡️ {selectedCard?.defense}</span>
                            </div>
                        </div>

                        <div className="vs-indicator">⚔️</div>

                        <div className={`battle-card enemy-card ${turnWinner === 'enemy' ? 'winner' : turnWinner === 'player' ? 'loser' : ''}`}>
                            <span className="card-emoji">{enemyCard?.emoji}</span>
                            <span className="card-title">{enemyCard?.title}</span>
                            <div className="card-stats">
                                <span>⚔️ {enemyCard?.power}</span>
                                <span>🛡️ {enemyCard?.defense}</span>
                            </div>
                        </div>
                    </div>
                )}

                {battleResult && (
                    <div className={`battle-result ${battleResult.type}`}>
                        <span className="result-icon">
                            {battleResult.type === 'win' ? '🎉' : battleResult.type === 'lose' ? '💔' : '🤝'}
                        </span>
                        <span className="result-message">{battleResult.message}</span>
                        <button className="next-turn-btn" onClick={nextTurn}>
                            Next Turn →
                        </button>
                    </div>
                )}
            </div>

            {/* Player Hand */}
            <div className="player-hand">
                <div className="hand-label">
                    Your Hand ({playerHand.length} cards) - Deck: {playerDeck.length}
                </div>
                <div className="hand-cards">
                    {playerHand.map((card) => (
                        <div
                            key={card.id}
                            className={`hand-card ${selectedCard?.id === card.id ? 'selected' : ''}`}
                            onClick={() => selectCard(card)}
                        >
                            <span className="card-emoji">{card.emoji}</span>
                            <span className="card-title">{card.title}</span>
                            <p className="card-desc">{card.description?.substring(0, 50)}...</p>
                            <div className="card-stats">
                                <span className="stat attack">⚔️ {card.power}</span>
                                <span className="stat defense">🛡️ {card.defense}</span>
                            </div>
                        </div>
                    ))}
                </div>

                {selectedCard && battlePhase === 'select' && (
                    <button className="play-card-btn" onClick={playCard}>
                        Play Card! ⚔️
                    </button>
                )}
            </div>

            {/* Pause Menu */}
            {isPaused && (
                <PauseMenu
                    gameName="Knowledge Duel"
                    score={score}
                    stats={{
                        rounds: round,
                        cardsPlayed
                    }}
                    onResume={() => setIsPaused(false)}
                    onRestart={handleRestart}
                    onQuit={onQuit}
                />
            )}

            {/* Game Complete */}
            {isComplete && (
                <GameComplete
                    victory={victory}
                    gameName="Knowledge Duel"
                    score={score}
                    xpEarned={xpEarned}
                    coinsEarned={coinsEarned}
                    stars={victory ? (playerHealth > maxPlayerHealth * 0.7 ? 3 : playerHealth > maxPlayerHealth * 0.4 ? 2 : 1) : 0}
                    accuracy={Math.round((playerHealth / maxPlayerHealth) * 100)}
                    stats={{
                        rounds: round,
                        cardsPlayed,
                        remainingHealth: playerHealth
                    }}
                    onPlayAgain={handleRestart}
                    onBackToMap={onQuit}
                    topic={topic}
                />
            )}
        </div>
    );
}

export default CardBattlerGame;
